/**
 * Utilidad para registrar eventos de tracking/analytics
 * Envía eventos al backend para análisis de uso
 */

const API_BASE_URL = 'https://fitos.es'; // Cambiar según tu entorno
const X_API_FITOS_KEY = 'xK9mP2vL8nQ4wR7jT5bY3cF6hA1dG0sZ';

/**
 * Registra un evento en el backend
 * @param {string} tipoEvento - Tipo de evento ('page_load', 'click', 'search', etc.)
 * @param {object} datos - Datos adicionales del evento
 * @returns {Promise} Promesa que se resuelve cuando el evento se registra
 */
export async function registrarEvento(tipoEvento, datos = {}) {
  try {
    const payload = {
      tipo_evento: tipoEvento,
      pagina: window.location.pathname,
      ...datos
    };

    // Para eventos críticos, usar fetch con keepalive (sendBeacon no permite headers personalizados)
    if (tipoEvento === 'page_unload' || tipoEvento === 'beforeunload') {
      const response = await fetch(`${API_BASE_URL}/api/registrar_evento`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-fitos': X_API_FITOS_KEY
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
      return;
    }

    // Para otros eventos, usar fetch normal
    const response = await fetch(`${API_BASE_URL}/api/registrar_evento`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-fitos': X_API_FITOS_KEY
      },
      body: JSON.stringify(payload),
      // No esperar respuesta para no bloquear la UI
      keepalive: true
    });

    if (!response.ok) {
      console.warn('Error al registrar evento:', response.statusText);
    }
  } catch (error) {
    // No fallar la aplicación si hay error al registrar
    console.warn('Error al registrar evento (no crítico):', error);
  }
}

/**
 * Helper para registrar clicks
 */
export function registrarClick(elemento, seccion = null, datosAdicionales = {}) {
  registrarEvento('click', {
    elemento,
    seccion,
    datos_adicionales: datosAdicionales
  });
}

/**
 * Helper para registrar búsquedas
 */
export function registrarBusqueda(termino, resultados = null) {
  registrarEvento('search', {
    elemento: 'busqueda',
    datos_adicionales: {
      termino,
      resultados_encontrados: resultados
    }
  });
}

/**
 * Helper para registrar preguntas en el chat
 * Registra cada pregunta que el usuario hace al asistente
 */
export function registrarPreguntaChat(pregunta, conversationId = null) {
  registrarEvento('chat_message', {
    seccion: 'chat',
    elemento: 'input-chat',
    datos_adicionales: {
      pregunta: pregunta,
      longitud_pregunta: pregunta.length,
      palabras: pregunta.trim().split(/\s+/).length,
      conversation_id: conversationId,
      timestamp: new Date().toISOString(),
      // Detectar si pregunta por exportar/descargar
      contiene_exportar: /export|descarg|excel|descarga/i.test(pregunta),
      // Detectar si pregunta por un producto específico
      contiene_producto: /flint|confidor|glifosato|trifloxistrobin/i.test(pregunta.toLowerCase())
    }
  });
}

/**
 * Helper para registrar descargas
 */
export function registrarDescarga(nombreArchivo, tipoArchivo = 'excel') {
  registrarEvento('download', {
    elemento: 'descarga',
    datos_adicionales: {
      nombre_archivo: nombreArchivo,
      tipo_archivo: tipoArchivo
    }
  });
}

/**
 * Helper para registrar visualización de sección
 */
export function registrarVisualizacionSeccion(seccion, tiempoEnPagina = null) {
  registrarEvento('section_view', {
    seccion,
    datos_adicionales: {
      tiempo_en_pagina: tiempoEnPagina || (Date.now() - window.pageLoadTime)
    }
  });
}

/**
 * Helper para registrar envío de formulario
 */
export function registrarFormulario(tipoFormulario, datos = {}) {
  registrarEvento('form_submit', {
    elemento: tipoFormulario,
    datos_adicionales: datos
  });
}

/**
 * Helper para registrar navegación
 */
export function registrarNavegacion(paginaDestino, paginaOrigen = null) {
  registrarEvento('navigation', {
    pagina: paginaDestino,
    datos_adicionales: {
      pagina_origen: paginaOrigen || window.location.pathname
    }
  });
}

/**
 * Inicializa el tracking de la página
 * Debe llamarse cuando se carga el componente
 */
// Variable para evitar múltiples inicializaciones
let trackingInicializado = false;

export function inicializarTracking() {
  // Evitar múltiples inicializaciones
  if (trackingInicializado) {
    return;
  }
  trackingInicializado = true;

  // Guardar tiempo de carga
  window.pageLoadTime = Date.now();

  // Registrar carga de página
  registrarEvento('page_load', {
    datos_adicionales: {
      url_completa: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || 'directo'
    }
  });

  // Registrar cuando el usuario abandona la página
  const handleBeforeUnload = () => {
    registrarEvento('page_unload', {
      datos_adicionales: {
        tiempo_en_pagina: Date.now() - (window.pageLoadTime || Date.now())
      }
    });
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // También registrar cuando se oculta la pestaña (Page Visibility API)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      registrarEvento('page_hidden', {
        datos_adicionales: {
          tiempo_en_pagina: Date.now() - (window.pageLoadTime || Date.now())
        }
      });
    }
  });
}

